import React, { useState, useEffect, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { makeStyles } from "@mui/styles";
import { Button, Row, Col, Modal, ModalBody, Form, FormGroup, Label, Input } from "reactstrap";
import AppHeader from "../../../Layout/AppHeader";
import AppSidebar from "../../../Layout/AppSidebar";
import GetAdminService from "../../../services/admin/GetAdminService";
import RegisterService from "../../../services/RegisterService";
import CustomLoader from "../../customLoader/CustomLoader";
import { Fragment } from "react";

const useStyles = makeStyles({
  root: {
    "& .super-app-theme--header": {
      fontSize: "14px"
    }
  }
});

const LoginAnalytics = () => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [currentSearchPayload, setCurrentSearchPayload] = useState(null);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortType, setSortType] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [firstTimeLoginCount, setFirstTimeLoginCount] = useState(0);
  const [registeredCount, setRegisteredCount] = useState(0);
  const [isViewingAppliedFilters, setIsViewingAppliedFilters] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedDownloadType, setSelectedDownloadType] = useState("reg_count");

  // Customize Query Modal States
  const [districtList, setDistrictList] = useState([]);
  const [schoolList, setSchoolList] = useState([]);
  const [gradeList, setGradeList] = useState([]);
  const [showDistContainer, setShowDistContainer] = useState(false);
  const [queryFormData, setQueryFormData] = useState({
    schoolDistrict: "",
    schoolDistrictId: "",
    schoolName: "",
    role: "",
    currentGrade: "",
    startDate: "",
    endDate: ""
  });
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const timeoutRef = useRef();
  const distRef = useRef();
  const today = new Date().toISOString().split("T")[0];

  const classes = useStyles();

  const getAnalyticsData = async (currentPage = 1, searchPayload = null, currentSortBy = null, currentSortType = null, forcePost = false) => {
    try {
      setLoading(true);
      let response;

      // Check if we have active filters (search payload)
      const hasActiveFilters = searchPayload && Object.keys(searchPayload).length > 0;
      const hasStoredFilters = !searchPayload && currentSearchPayload && Object.keys(currentSearchPayload).length > 0;

      if (hasActiveFilters || hasStoredFilters || forcePost) {
        // Use POST for filtered search
        const payloadToUse = forcePost ? searchPayload : hasActiveFilters ? searchPayload : currentSearchPayload;

        // Create API payload excluding district_name if present
        const apiPayload = { ...payloadToUse };
        delete apiPayload.district_name;

        apiPayload.page = currentPage;
        apiPayload.sort_by = currentSortBy || sortBy || undefined;
        apiPayload.sort_type = currentSortType || sortType || undefined;

        // Remove undefined values
        Object.keys(apiPayload).forEach((key) => {
          if (apiPayload[key] === undefined) {
            delete apiPayload[key];
          }
        });

        response = await GetAdminService.getLoginAnalytics(apiPayload);
      } else {
        // Use GET for default/sort
        const params = {
          page: currentPage,
          sort_by: currentSortBy || sortBy || undefined,
          sort_type: currentSortType || sortType || undefined
        };

        response = await GetAdminService.getLoginAnalyticsWithParams(params);
      }

      if (response.data) {
        // Check if results are nested or direct
        const nestedResults = response.data.results || {};
        const results = nestedResults.results || response.data.results || [];
        const count = response.data.count || 0;

        // Update summary counts if available
        if (typeof nestedResults.first_time_login_count === "number") {
          setFirstTimeLoginCount(nestedResults.first_time_login_count);
        }
        if (typeof nestedResults.registered_count === "number") {
          setRegisteredCount(nestedResults.registered_count);
        }

        // Transform the data to add unique IDs for DataGrid
        const transformedData = results.map((item, index) => ({
          ...item,
          uniqueId: `${item.id}-${currentPage}-${index}`
        }));

        setAnalyticsData(transformedData);
        setTotalCount(count);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAnalyticsData(page);
  }, [page]);

  // Load grades when component mounts
  useEffect(() => {
    loadGrades();
  }, []);

  const loadGrades = async () => {
    try {
      setLoadingGrades(true);
      const response = await GetAdminService.getGradeList();
      if (response?.data?.grade) {
        // Transform the response data to match the expected format
        const gradeData = Object.entries(response.data.grade).map(([key, value]) => ({
          id: value,
          name: key,
          displayName: key
        }));
        setGradeList(gradeData);
      }
    } catch (error) {
    } finally {
      setLoadingGrades(false);
    }
  };

  const getSchoolDataDistrictwise = (districtVal) => {
    if (!districtVal) {
      setSchoolList([]);
      return;
    }

    setLoadingSchools(true);
    RegisterService.getDistrictbaseSchoolNameList(districtVal)
    .then((res) => {
      if (res?.status === 200 && res?.data?.objects && res?.data?.objects.length > 0) {
        setSchoolList(res.data.objects);
      } else {
        setSchoolList([]);
      }
    })
    .catch((_error) => {
      setSchoolList([]);
    })
    .finally(() => {
      setLoadingSchools(false);
    });
  };

  const handleDistChange = () => {
    let value = distRef.current.value;
    setQueryFormData({ ...queryFormData, schoolDistrict: value, schoolName: "" });
    clearTimeout(timeoutRef.current);

    if (!value) {
      setShowDistContainer(false);
      setDistrictList([]);
      setSchoolList([]);
    } else {
      timeoutRef.current = setTimeout(() => {
        if (value !== "" && value.length >= 3) {
          setLoadingDistricts(true);
          let query = { q: value };

          RegisterService.getDistrictList(query)
          .then((data) => {
            setDistrictList(data?.data?.objects || []);
            setShowDistContainer(true);
          })
          .catch((_error) => {
            setDistrictList([]);
          })
          .finally(() => {
            setLoadingDistricts(false);
          });
        }
      }, 1000);
    }
  };

  const handleQueryFormChange = (e) => {
    const { name, value } = e.target;

    if (name === "role") {
      if (value !== "student") {
        setQueryFormData({ ...queryFormData, [name]: value, currentGrade: "" });
      } else {
        setQueryFormData({ ...queryFormData, [name]: value });
      }
    } else if (name === "startDate") {
      // Ensure start date is not after end date
      const newStartDate = value;
      const updatedData = { ...queryFormData, startDate: newStartDate };
      if (queryFormData.endDate && newStartDate > queryFormData.endDate) {
        updatedData.endDate = newStartDate;
      }
      setQueryFormData(updatedData);
    } else if (name === "endDate") {
      // Ensure end date is not before start date
      const newEndDate = value;
      const updatedData = { ...queryFormData, endDate: newEndDate };
      if (queryFormData.startDate && newEndDate < queryFormData.startDate) {
        updatedData.startDate = newEndDate;
      }
      setQueryFormData(updatedData);
    } else {
      setQueryFormData({ ...queryFormData, [name]: value });
    }
  };

  const handleDistrictSelect = (district) => {
    setShowDistContainer(false);
    setQueryFormData({
      ...queryFormData,
      schoolDistrict: district.name,
      schoolDistrictId: district.id,
      schoolName: "",
      role: "",
      currentGrade: ""
    });
    getSchoolDataDistrictwise(district.id);
  };

  const handleCustomizeSearch = async () => {
    try {
      setShowCustomizeModal(false);

      // Prepare payload for API
      const searchPayload = {
        district_id: queryFormData.schoolDistrictId || null,
        district_name: queryFormData.schoolDistrict || null,
        school_ids: queryFormData.schoolName ? [queryFormData.schoolName] : null,
        role: queryFormData.role || null,
        current_grade: queryFormData.currentGrade || null,
        start_date: queryFormData.startDate || null,
        end_date: queryFormData.endDate || null
      };

      // Remove null values from payload
      const cleanPayload = Object.fromEntries(
        Object.entries(searchPayload).filter(([_, value]) => value !== null)
      );

      // Create API payload (exclude district_name as it's only for UI)
      const apiPayload = { ...cleanPayload };
      delete apiPayload.district_name;


      // Store the search payload for pagination and set filter active
      const hasFilters = Object.keys(cleanPayload).length > 0;
      setCurrentSearchPayload(hasFilters ? cleanPayload : null);
      setIsFilterActive(hasFilters);

      // Reset to page 1 and call API with search filters
      setPage(1);
      await getAnalyticsData(1, apiPayload);

    } catch (error) {
    }
  };

  const resetCustomizeForm = async () => {
    // Clear the form values
    setQueryFormData({
      schoolDistrict: "",
      schoolDistrictId: "",
      schoolName: "",
      role: "",
      currentGrade: "",
      startDate: "",
      endDate: ""
    });
    setDistrictList([]);
    setSchoolList([]);
    setShowDistContainer(false);

    // Clear the district input field
    if (distRef.current) {
      distRef.current.value = "";
    }

    // Clear filters and call POST API with only page parameter
    setCurrentSearchPayload(null);
    setIsFilterActive(false);
    setSortBy(null);
    setSortType(null);
    setSortModel([]);
    setShowCustomizeModal(false);
    setPage(1);
    await getAnalyticsData(1, {}, null, null, true);
  };

  const clearModalForm = () => {
    setQueryFormData({
      schoolDistrict: "",
      schoolDistrictId: "",
      schoolName: "",
      role: "",
      currentGrade: "",
      startDate: "",
      endDate: ""
    });
    setDistrictList([]);
    setSchoolList([]);
    setShowDistContainer(false);
    setIsViewingAppliedFilters(false);

    // Clear the district input field
    if (distRef.current) {
      distRef.current.value = "";
    }
  };

  const populateFormFromAppliedFilters = async () => {
    if (!currentSearchPayload) return;


    const formData = {
      schoolDistrict: "",
      schoolDistrictId: "",
      schoolName: "",
      role: "",
      currentGrade: "",
      startDate: "",
      endDate: ""
    };

    // Populate form data from currentSearchPayload
    if (currentSearchPayload.district_id) {
      formData.schoolDistrictId = currentSearchPayload.district_id;
      // Use the stored district name from the payload
      if (currentSearchPayload.district_name) {
        formData.schoolDistrict = currentSearchPayload.district_name;
      } else {
        // Fallback: try to find district name from current data
        if (analyticsData.length > 0) {
          const sampleRecord = analyticsData.find((item) => item.school__district__name);
          if (sampleRecord && sampleRecord.school__district__name) {
            formData.schoolDistrict = sampleRecord.school__district__name;
          } else {
          }
        }
      }
    }

    if (currentSearchPayload.school_ids && currentSearchPayload.school_ids.length > 0) {
      formData.schoolName = currentSearchPayload.school_ids[0];
      // Load schools for the selected district when we have district ID
      if (formData.schoolDistrictId) {
        getSchoolDataDistrictwise(formData.schoolDistrictId);
      }
    } else if (formData.schoolDistrictId) {
      // Even if no school is selected, load the schools for the district
      getSchoolDataDistrictwise(formData.schoolDistrictId);
    }

    if (currentSearchPayload.role) {
      formData.role = currentSearchPayload.role;
    }

    if (currentSearchPayload.current_grade) {
      formData.currentGrade = currentSearchPayload.current_grade;
    }

    if (currentSearchPayload.start_date) {
      formData.startDate = currentSearchPayload.start_date;
    }

    if (currentSearchPayload.end_date) {
      formData.endDate = currentSearchPayload.end_date;
    }

    setQueryFormData(formData);
    setIsViewingAppliedFilters(true);


    // Set district input field value after a small delay to ensure the ref is available
    setTimeout(() => {
      if (distRef.current && formData.schoolDistrict) {
        distRef.current.value = formData.schoolDistrict;
      } else {
      }
    }, 100);
  };

  const switchToEditMode = () => {
    setIsViewingAppliedFilters(false);
  };

  const handleSortModelChange = (newSortModel) => {
    if (newSortModel.length === 0) {
      setSortModel([]);
      setSortBy(null);
      setSortType(null);
      getAnalyticsData(page, currentSearchPayload, null, null);
      return;
    }

    const { field, sort } = newSortModel[0];
    // MUI gives 'asc' or 'desc'; default to 'asc' if somehow missing
    const nextSort = sort || 'asc';

    setSortModel([{ field, sort: nextSort }]);
    setSortBy(field);
    setSortType(nextSort);
    getAnalyticsData(page, currentSearchPayload, field, nextSort);
  };

  const columns = [
  {
    field: "username",
    headerName: "Username",
    headerClassName: "super-app-theme--header",
    width: 180,
    sortable: true,
    renderCell: (params) =>
    <div style={{
      fontSize: '13px',
      color: '#1e40af',
      fontWeight: '600',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }} title={params.value || 'No username'}>
          {params.value || 'N/A'}
        </div>

  },
  {
    field: "email",
    headerName: "Email",
    headerClassName: "super-app-theme--header",
    width: 200,
    sortable: true,
    renderCell: (params) =>
    <div style={{
      fontSize: '12px',
      color: '#374151',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }} title={params.value || 'No email'}>
          {params.value || 'N/A'}
        </div>

  },
  {
    field: "first_name",
    headerName: "First name",
    headerClassName: "super-app-theme--header",
    width: 130,
    sortable: true,
    renderCell: (params) =>
    <div style={{
      fontWeight: '600',
      color: '#059669',
      fontSize: '13px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }} title={params.value || '-'}>
          {params.value || '-'}
        </div>

  },
  {
    field: "last_name",
    headerName: "Last name",
    headerClassName: "super-app-theme--header",
    width: 130,
    sortable: true,
    renderCell: (params) =>
    <div style={{
      fontWeight: '600',
      color: '#059669',
      fontSize: '13px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }} title={params.value || '-'}>
          {params.value || '-'}
        </div>

  },
  {
    field: "role",
    headerName: "Role",
    headerClassName: "super-app-theme--header",
    width: 100,
    sortable: true,
    renderCell: (params) =>
    <div style={{
      backgroundColor: params.value === 'student' ? '#dbeafe' : params.value === 'counselor' ? '#fed7d7' : '#e5e7eb',
      color: params.value === 'student' ? '#1e40af' : params.value === 'counselor' ? '#dc2626' : '#374151',
      padding: '4px 10px',
      borderRadius: '8px',
      fontSize: '11px',
      fontWeight: '600',
      textAlign: 'center',
      textTransform: 'capitalize',
      display: 'inline-block'
    }}>
          {params.value || 'N/A'}
        </div>

  },
  {
    field: "current_grade",
    headerName: "Grade",
    headerClassName: "super-app-theme--header",
    width: 90,
    sortable: true,
    renderCell: (params) =>
    <div style={{
      backgroundColor: '#dbeafe',
      color: '#1e40af',
      padding: '4px 10px',
      borderRadius: '8px',
      fontSize: '11px',
      fontWeight: '700',
      textAlign: 'center',
      display: 'inline-block'
    }}>
          {params.value ? `${params.value}` : 'N/A'}
        </div>

  },
  {
    field: "student_id",
    headerName: "Student id",
    headerClassName: "super-app-theme--header",
    width: 110,
    sortable: true,
    renderCell: (params) =>
    <div style={{
      fontSize: '12px',
      color: '#374151',
      fontWeight: '600'
    }}>
          {params.value || 'N/A'}
        </div>

  },
  {
    field: "parent_email",
    headerName: "Parent email",
    headerClassName: "super-app-theme--header",
    width: 180,
    sortable: true,
    renderCell: (params) =>
    <div style={{
      fontSize: '12px',
      color: '#6b7280',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }} title={params.value || 'No parent email'}>
          {params.value || 'N/A'}
        </div>

  },
  {
    field: "parent_mobile",
    headerName: "Parent phone",
    headerClassName: "super-app-theme--header",
    width: 130,
    sortable: true,
    renderCell: (params) =>
    <div style={{
      fontSize: '12px',
      color: '#6b7280',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }} title={params.value || 'No mobile'}>
          {params.value || 'N/A'}
        </div>

  },
  {
    field: "gender",
    headerName: "Gender",
    headerClassName: "super-app-theme--header",
    width: 90,
    sortable: true,
    renderCell: (params) =>
    <div style={{
      backgroundColor: params.value === 'Male' || params.value === 'M' ? '#dbeafe' : params.value === 'Female' || params.value === 'F' ? '#fce7f3' : '#f3f4f6',
      color: params.value === 'Male' || params.value === 'M' ? '#1e40af' : params.value === 'Female' || params.value === 'F' ? '#be185d' : '#6b7280',
      padding: '4px 10px',
      borderRadius: '8px',
      fontSize: '11px',
      fontWeight: '600',
      textAlign: 'center',
      display: 'inline-block'
    }}>
          {params.value ? params.value.substring(0, 1) : 'N/A'}
        </div>

  },
  {
    field: "gpa",
    headerName: "Gpa",
    headerClassName: "super-app-theme--header",
    width: 75,
    sortable: true,
    renderCell: (params) => {
      const gpaValue = parseFloat(params.value) || 0;
      return (
        <div style={{
          backgroundColor: gpaValue >= 3.5 ? '#d1fae5' : gpaValue >= 3.0 ? '#fef3c7' : gpaValue >= 2.5 ? '#fee2e2' : '#f3f4f6',
          color: gpaValue >= 3.5 ? '#059669' : gpaValue >= 3.0 ? '#d97706' : gpaValue >= 2.5 ? '#dc2626' : '#6b7280',
          padding: '4px 8px',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: '700',
          textAlign: 'center',
          display: 'inline-block'
        }}>
            {gpaValue.toFixed(2)}
          </div>);

    }
  },
  {
    field: "ethnicity",
    headerName: "Ethnicity",
    headerClassName: "super-app-theme--header",
    width: 120,
    sortable: true,
    renderCell: (params) =>
    <div style={{
      fontSize: '12px',
      color: '#4b5563',
      fontWeight: '500',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }} title={params.value || 'Not specified'}>
          {params.value || 'N/A'}
        </div>

  },
  {
    field: "school__name",
    headerName: "School",
    headerClassName: "super-app-theme--header",
    width: 180,
    sortable: true,
    renderCell: (params) =>
    <div style={{
      fontSize: '12px',
      color: '#374151',
      fontWeight: '500',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }} title={params.value || 'No school specified'}>
          {params.value || 'N/A'}
        </div>

  },
  {
    field: "school__district__name",
    headerName: "District",
    headerClassName: "super-app-theme--header",
    width: 160,
    sortable: true,
    renderCell: (params) =>
    <div style={{
      fontSize: '12px',
      color: '#374151',
      fontWeight: '500',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }} title={params.value || 'No district specified'}>
          {params.value || 'N/A'}
        </div>

  },
  {
    field: "last_login",
    headerName: "Last Login Date",
    headerClassName: "super-app-theme--header",
    width: 110,
    sortable: true,
    renderCell: (params) => {
      const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };

      return (
        <div style={{
          fontSize: '11px',
          color: '#6b7280',
          fontWeight: '500',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }} title={formatDate(params.value)}>
            {formatDate(params.value)}
          </div>);

    }
  }];


  const handlePageChange = (newPage) => {
    setPage(newPage + 1); // MUI DataGrid is 0-indexed, but API is 1-indexed
  };

  const isDateRangeSelected = queryFormData.startDate && queryFormData.endDate;
  const isOtherFilterSelected = !!(
  queryFormData.schoolDistrictId ||
  queryFormData.schoolName ||
  queryFormData.role ||
  queryFormData.currentGrade);

  const isSearchEnabled = !!(isDateRangeSelected || isOtherFilterSelected);

  const clearModalFormOnly = () => {
    // Only clear the form values, don't close modal or call API
    setQueryFormData({
      schoolDistrict: "",
      schoolDistrictId: "",
      schoolName: "",
      role: "",
      currentGrade: "",
      startDate: "",
      endDate: ""
    });
    setDistrictList([]);
    setSchoolList([]);
    setShowDistContainer(false);

    // Clear the district input field
    if (distRef.current) {
      distRef.current.value = "";
    }
  };

  const handleExportData = async () => {
    // Show export modal instead of directly exporting
    setShowExportModal(true);
  };

  const handleConfirmExport = async () => {
    try {
      setExporting(true);
      setShowExportModal(false);

      // Build payload based on current filters and sorting.
      const exportPayload = {
        download_type: selectedDownloadType
      };

      if (currentSearchPayload && Object.keys(currentSearchPayload).length > 0) {
        Object.assign(exportPayload, currentSearchPayload);
        // Remove district_name from export payload as it's only for UI
        delete exportPayload.district_name;
      }

      if (sortBy && sortType) {
        exportPayload.sort_by = sortBy;
        exportPayload.sort_type = sortType;
      }

      const response = await GetAdminService.exportLoginAnalytics(exportPayload);

      if (response?.data?.download_url) {
        window.open(response.data.download_url);
      } else {
      }
    } catch (error) {
    } finally {
      setExporting(false);
    }
  };

  const handleExportModalClose = () => {
    setShowExportModal(false);
    setSelectedDownloadType("reg_count"); // Reset to default
  };

  return (
    <Fragment>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>
      <AppHeader />
      <div className="app-main">
        <AppSidebar />
        <div className="app-main__outer">
          {loading ?
          <CustomLoader /> :

          <div className="csv-import-container">
              <div className="flex canned-message-heading" style={{
              padding: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: 'white',
              borderRadius: '12px',
              marginBottom: '10px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              gap: '12px'
            }}>
                <p style={{ margin: 0 }}>
                  <span style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                    Login Analytics
                  </span>
                </p>
                <div style={{ marginTop: '6px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{
                  padding: '6px 12px',
                  borderRadius: '999px',
                  backgroundColor: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#1d4ed8',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                    <span style={{ fontSize: '14px' }}>🆕</span>
                    <span>Login Count: {firstTimeLoginCount}</span>
                  </div>
                  <div style={{
                  padding: '6px 12px',
                  borderRadius: '999px',
                  backgroundColor: '#ecfdf5',
                  border: '1px solid #bbf7d0',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#047857',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                    <span style={{ fontSize: '14px' }}>✅</span>
                    <span>Registered Count: {registeredCount}</span>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                  onClick={handleExportData}
                  disabled={exporting}
                  style={{
                    background: '#1395f2',
                    border: 'none',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: exporting ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 15px rgba(19, 149, 242, 0.4)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: exporting ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!exporting) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(19, 149, 242, 0.6)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(19, 149, 242, 0.4)';
                  }}>
                  
                  <span style={{ fontSize: '18px' }}>⬇️</span>
                  {exporting ? 'Exporting...' : 'Export Data'}
                </button>
                <button
                  onClick={() => {
                    setShowCustomizeModal(true);
                    if (isFilterActive && currentSearchPayload) {
                      populateFormFromAppliedFilters();
                    } else {
                      clearModalForm();
                    }
                  }}
                  style={{
                    background: isFilterActive ? 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    color: 'white',
                    padding: '12px 28px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: isFilterActive ? '0 4px 15px rgba(220, 38, 38, 0.4)' : '0 4px 15px rgba(102, 126, 234, 0.4)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = isFilterActive ? '0 6px 20px rgba(220, 38, 38, 0.6)' : '0 6px 20px rgba(102, 126, 234, 0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = isFilterActive ? '0 4px 15px rgba(220, 38, 38, 0.4)' : '0 4px 15px rgba(102, 126, 234, 0.4)';
                  }}>
                  
                  {isFilterActive &&
                  <span style={{
                    position: 'absolute',
                    top: '-6px',
                    right: '-6px',
                    width: '20px',
                    height: '20px',
                    backgroundColor: '#fbbf24',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '700',
                    border: '2px solid white',
                    animation: 'pulse 2s infinite'
                  }}>!</span>}
                  
                  <span style={{ fontSize: '18px' }}>🔍</span>
                  Customize Query
                </button>
                
                {isFilterActive &&
                <button
                  onClick={resetCustomizeForm}
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    border: 'none',
                    color: 'white',
                    padding: '12px 28px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(245, 158, 11, 0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(245, 158, 11, 0.4)';
                  }}>
                  
                    <span style={{ fontSize: '18px' }}>🔄</span>
                    Clear All Filters
                  </button>}
                
                </div>
              </div>

              {/* <div style={{ 
               margin: '10px 10px 15px 10px',
               padding: '12px 20px',
               backgroundColor: '#fef3c7',
               border: '2px solid #fbbf24',
               borderRadius: '10px',
               display: 'flex',
               alignItems: 'center',
               gap: '10px',
               boxShadow: '0 2px 8px rgba(251, 191, 36, 0.2)'
              }}>
               <span style={{ 
                 fontSize: '20px',
                 filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
               }}>📊</span>
               <p style={{ 
                 margin: 0,
                 fontSize: '14px',
                 color: '#92400e',
                 fontWeight: '600',
                 letterSpacing: '0.3px'
               }}>
                 <span style={{ 
                   fontWeight: '700',
                   color: '#78350f'
                 }}>Note:</span> This data shown based on current month
               </p>
              </div> */}

              <div style={{ margin: "20px 10px 10px 10px", overflowX: "hidden" }}>
                <div
                className={classes.root}
                style={{
                  height: 'calc(100vh - 180px)',
                  width: '100%',
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                  border: '1px solid #e5e7eb'
                }}>
                
                  <DataGrid
                  rows={analyticsData}
                  columns={columns}
                  pageSize={pageSize}
                  pagination
                  paginationMode="server"
                  rowCount={totalCount}
                  page={page - 1} // MUI DataGrid is 0-indexed
                  onPageChange={handlePageChange}
                  loading={loading}
                  getRowId={(row) => row.uniqueId} // Use unique ID
                  disableSelectionOnClick
                  disableColumnMenu
                  hideFooterSelectedRowCount
                  rowHeight={52}
                  headerHeight={48}
                  sortingMode="server"
                  sortingOrder={['asc', 'desc']}
                  sortModel={sortModel}
                  onSortModelChange={handleSortModelChange}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f8fafc',
                      borderBottom: '2px solid #e2e8f0',
                      fontSize: '13px',
                      fontWeight: '700',
                      color: '#1f2937',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: '700'
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid #f1f5f9',
                      fontSize: '13px',
                      padding: '0 12px'
                    },
                    '& .MuiDataGrid-row': {
                      transition: 'background-color 0.2s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f0f9ff'
                      }
                    },
                    '& .MuiDataGrid-row:nth-of-type(even)': {
                      backgroundColor: '#fafafa'
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '2px solid #e2e8f0',
                      backgroundColor: '#f8fafc',
                      minHeight: '56px',
                      padding: '0 20px',
                      overflow: 'hidden'
                    },
                    '& .MuiTablePagination-root': {
                      overflow: 'hidden'
                    },
                    '& .MuiTablePagination-displayedRows': {
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      margin: 0
                    },
                    '& .MuiTablePagination-selectLabel': {
                      display: 'none'
                    },
                    '& .MuiTablePagination-select': {
                      display: 'none'
                    },
                    '& .MuiTablePagination-spacer': {
                      flex: 'none'
                    },
                    '& .MuiTablePagination-toolbar': {
                      minHeight: '56px',
                      paddingLeft: '0',
                      paddingRight: '0',
                      overflow: 'hidden'
                    },
                    '& .MuiTablePagination-actions': {
                      marginLeft: '20px',
                      overflow: 'hidden',
                      '& .MuiIconButton-root': {
                        backgroundColor: '#1395f2',
                        color: 'white',
                        borderRadius: '8px',
                        padding: '6px',
                        marginLeft: '8px',
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#0d7fd1'
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#e5e7eb',
                          color: '#9ca3af'
                        }
                      }
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px'
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f5f9',
                        borderRadius: '4px'
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#cbd5e1',
                        borderRadius: '4px',
                        '&:hover': {
                          background: '#94a3b8'
                        }
                      }
                    }
                  }} />
                
                </div>
              </div>
            </div>}
          
        </div>
      </div>
      
      {/* Customize Query Modal */}
      <Modal
        isOpen={showCustomizeModal}
        toggle={() => setShowCustomizeModal(false)}
        size="lg"
        centered
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 28px',
          borderBottom: '2px solid #e5e7eb',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}>
          <h4 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '700',
            color: 'white'
          }}>
            {isViewingAppliedFilters ? '📋 Current Applied Filters' : '🔍 Customize Search Query'}
          </h4>
          <button
            type="button"
            onClick={() => setShowCustomizeModal(false)}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              fontSize: '28px',
              color: 'white',
              cursor: 'pointer',
              padding: '2px 10px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              lineHeight: '1'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
              e.target.style.transform = 'rotate(90deg)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              e.target.style.transform = 'rotate(0deg)';
            }}>
            
            ×
          </button>
        </div>
        <ModalBody style={{ padding: '32px', backgroundColor: '#fafbfc' }}>
          {isViewingAppliedFilters &&
          <div style={{
            backgroundColor: '#e0f2fe',
            border: '2px solid #0891b2',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
              <span style={{ fontSize: '20px' }}>ℹ️</span>
              <div>
                <p style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: '600',
                color: '#0c4a6e',
                marginBottom: '4px'
              }}>
                  Currently Viewing Applied Filters
                </p>
                <p style={{
                margin: 0,
                fontSize: '12px',
                color: '#075985'
              }}>
                  These are the filters currently applied to the data. Click "Edit Filters" to modify them.
                </p>
              </div>
            </div>}
          
          <Form>
            {/* Date Range Selection */}
            <Row className="mb-4">
              <Col md={6}>
                <FormGroup>
                  <Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Start Date
                  </Label>
                  <Input
                    type="date"
                    name="startDate"
                    value={queryFormData.startDate || ""}
                    onChange={handleQueryFormChange}
                    max={queryFormData.endDate || today}
                    disabled={isViewingAppliedFilters}
                    style={{
                      borderRadius: '10px',
                      border: '2px solid #e5e7eb',
                      padding: '12px 16px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: isViewingAppliedFilters ? '#f9fafb' : 'white',
                      cursor: isViewingAppliedFilters ? 'not-allowed' : 'text'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }} />
                  
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    End Date
                  </Label>
                  <Input
                    type="date"
                    name="endDate"
                    value={queryFormData.endDate || ""}
                    onChange={handleQueryFormChange}
                    min={queryFormData.startDate || undefined}
                    max={today}
                    disabled={isViewingAppliedFilters}
                    style={{
                      borderRadius: '10px',
                      border: '2px solid #e5e7eb',
                      padding: '12px 16px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: isViewingAppliedFilters ? '#f9fafb' : 'white',
                      cursor: isViewingAppliedFilters ? 'not-allowed' : 'text'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }} />
                  
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                {/* District Selection */}
                <FormGroup>
                  <Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Select District
                  </Label>
                  <div style={{ position: 'relative' }}>
                    <Input
                      type="text"
                      placeholder={isViewingAppliedFilters ? "Selected district" : "Type to search district..."}
                      innerRef={distRef}
                      value={queryFormData.schoolDistrict || ""}
                      onChange={handleDistChange}
                      autoComplete="off"
                      disabled={isViewingAppliedFilters}
                      style={{
                        borderRadius: '10px',
                        border: '2px solid #e5e7eb',
                        padding: '12px 16px',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        backgroundColor: isViewingAppliedFilters ? '#f9fafb' : 'white',
                        cursor: isViewingAppliedFilters ? 'not-allowed' : 'text'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                      }} />
                    
                    {loadingDistricts &&
                    <div style={{ padding: '8px', fontSize: '12px', color: '#666' }}>
                        Loading districts...
                      </div>}
                    
                    {showDistContainer && districtList.length > 0 && !isViewingAppliedFilters &&
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '2px solid #667eea',
                      borderRadius: '10px',
                      boxShadow: '0 10px 25px rgba(102, 126, 234, 0.2)',
                      zIndex: 1000,
                      maxHeight: '250px',
                      overflowY: 'auto',
                      marginTop: '8px'
                    }}>
                        {districtList.map((item, index) =>
                      <div
                        key={index}
                        onClick={() => handleDistrictSelect(item)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: index < districtList.length - 1 ? '1px solid #f3f4f6' : 'none',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#374151',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.backgroundColor = '#eff6ff';
                          e.target.style.paddingLeft = '20px';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.paddingLeft = '16px';
                        }}>
                        
                             {item.name}
                          </div>
                      )}
                      </div>}
                    
                  </div>
                </FormGroup>
                
                {/* Role Selection */}
                <FormGroup>
                  <Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Select Role
                  </Label>
                  <Input
                    type="select"
                    name="role"
                    value={queryFormData.role || ""}
                    onChange={handleQueryFormChange}
                    disabled={isViewingAppliedFilters || !isViewingAppliedFilters && !queryFormData.schoolName}
                    style={{
                      borderRadius: '10px',
                      border: '2px solid #e5e7eb',
                      padding: '12px 16px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: isViewingAppliedFilters || !isViewingAppliedFilters && !queryFormData.schoolName ? '#f9fafb' : 'white',
                      cursor: isViewingAppliedFilters || !isViewingAppliedFilters && !queryFormData.schoolName ? 'not-allowed' : 'pointer'
                    }}
                    onFocus={(e) => {
                      if (queryFormData.schoolName) {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}>
                    
                    <option value="">Select Role</option>
                    <option value="student">Student</option>
                    <option value="parent">Parent</option>
                  </Input>
                </FormGroup>
              </Col>
              
              <Col md={6}>
                {/* School Selection */}
                <FormGroup>
                  <Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    Select School Name
                  </Label>
                  <Input
                    type="select"
                    name="schoolName"
                    value={queryFormData.schoolName || ""}
                    onChange={handleQueryFormChange}
                    disabled={isViewingAppliedFilters || !queryFormData.schoolDistrictId || loadingSchools}
                    style={{
                      borderRadius: '10px',
                      border: '2px solid #e5e7eb',
                      padding: '12px 16px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: isViewingAppliedFilters || !queryFormData.schoolDistrictId || loadingSchools ? '#f9fafb' : 'white',
                      cursor: isViewingAppliedFilters || !queryFormData.schoolDistrictId || loadingSchools ? 'not-allowed' : 'pointer'
                    }}
                    onFocus={(e) => {
                      if (queryFormData.schoolDistrictId && !loadingSchools) {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}>
                    
                    <option value="">Select School</option>
                    {loadingSchools ?
                    <option disabled>Loading schools...</option> :

                    schoolList.map((item, index) =>
                    <option key={index} value={item.id}>
                          {item.name}
                        </option>
                    )}
                    
                  </Input>
                </FormGroup>
                
                {/* Current Grade (only for students) */}
                {queryFormData.role === "student" &&
                <FormGroup>
                    <Label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                      Current Grade
                    </Label>
                    <Input
                    type="select"
                    name="currentGrade"
                    value={queryFormData.currentGrade || ""}
                    onChange={handleQueryFormChange}
                    disabled={isViewingAppliedFilters || loadingGrades}
                    style={{
                      borderRadius: '10px',
                      border: '2px solid #e5e7eb',
                      padding: '12px 16px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: isViewingAppliedFilters || loadingGrades ? '#f9fafb' : 'white',
                      cursor: isViewingAppliedFilters || loadingGrades ? 'not-allowed' : 'pointer'
                    }}
                    onFocus={(e) => {
                      if (!loadingGrades) {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}>
                    
                      <option value="">Select Grade</option>
                      {loadingGrades ?
                    <option disabled>Loading grades...</option> :

                    gradeList.map((grade, index) =>
                    <option key={index} value={grade.id}>
                            {grade.displayName}
                          </option>
                    )}
                    
                    </Input>
                  </FormGroup>}
                
              </Col>
            </Row>
            
            {/* Action Buttons */}
            <div className="d-flex justify-content-between mt-4" style={{ paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <Button
                  onClick={() => setShowCustomizeModal(false)}
                  style={{
                    backgroundColor: '#f3f4f6',
                    borderColor: '#e5e7eb',
                    color: '#374151',
                    fontWeight: '600',
                    padding: '10px 24px',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    border: '1px solid #e5e7eb'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#e5e7eb';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f3f4f6';
                    e.target.style.transform = 'translateY(0)';
                  }}>
                  
                  ✕ Cancel
                </Button>
                {isViewingAppliedFilters ?
                <Button
                  onClick={switchToEditMode}
                  style={{
                    backgroundColor: '#dbeafe',
                    borderColor: '#3b82f6',
                    color: '#1d4ed8',
                    fontWeight: '600',
                    padding: '10px 24px',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    border: '1px solid #3b82f6'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#3b82f6';
                    e.target.style.color = 'white';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#dbeafe';
                    e.target.style.color = '#1d4ed8';
                    e.target.style.transform = 'translateY(0)';
                  }}>
                  
                    ✏️ Edit Filters
                  </Button> :

                <Button
                  onClick={clearModalFormOnly}
                  style={{
                    backgroundColor: '#fef3c7',
                    borderColor: '#fbbf24',
                    color: '#92400e',
                    fontWeight: '600',
                    padding: '10px 24px',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    border: '1px solid #fbbf24'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#fbbf24';
                    e.target.style.color = 'white';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#fef3c7';
                    e.target.style.color = '#92400e';
                    e.target.style.transform = 'translateY(0)';
                  }}>
                  
                    ↻ Reset
                  </Button>}
                
              </div>
              {!isViewingAppliedFilters &&
              <Button
                onClick={handleCustomizeSearch}
                disabled={!isSearchEnabled}
                style={{
                  background: isSearchEnabled ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e5e7eb',
                  borderColor: 'transparent',
                  color: 'white',
                  fontWeight: '700',
                  padding: '10px 32px',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  boxShadow: isSearchEnabled ? '0 4px 15px rgba(102, 126, 234, 0.4)' : 'none',
                  cursor: isSearchEnabled ? 'pointer' : 'not-allowed'
                }}
                onMouseEnter={(e) => {
                  if (isSearchEnabled) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isSearchEnabled) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                  }
                }}>
                
                  🔍 Search
                </Button>}
              
            </div>
          </Form>
        </ModalBody>
      </Modal>
      
      {/* Export Options Modal */}
      <Modal
        isOpen={showExportModal}
        toggle={handleExportModalClose}
        size="md"
        centered
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 28px',
          borderBottom: '2px solid #e5e7eb',
          background: '#1395f2'
        }}>
          <h4 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '700',
            color: 'white'
          }}>
            📊 Export Options
          </h4>
          <button
            type="button"
            onClick={handleExportModalClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              fontSize: '28px',
              color: 'white',
              cursor: 'pointer',
              padding: '2px 10px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              lineHeight: '1'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
              e.target.style.transform = 'rotate(90deg)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              e.target.style.transform = 'rotate(0deg)';
            }}>
            
            ×
          </button>
        </div>
        <ModalBody style={{ padding: '32px', backgroundColor: '#fafbfc' }}>
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '2px solid #0284c7',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '20px' }}>📋</span>
            <div>
              <p style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: '600',
                color: '#0c4a6e',
                marginBottom: '4px'
              }}>
                Select Export Type
              </p>
              <p style={{
                margin: 0,
                fontSize: '12px',
                color: '#075985'
              }}>
                Choose which data type you want to include in your export file.
              </p>
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px',
                  borderRadius: '12px',
                  border: selectedDownloadType === 'reg_count' ? '2px solid #0284c7' : '2px solid #e5e7eb',
                  backgroundColor: selectedDownloadType === 'reg_count' ? '#f0f9ff' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '12px'
                }}
                onMouseEnter={(e) => {
                  if (selectedDownloadType !== 'reg_count') {
                    e.target.style.backgroundColor = '#f8fafc';
                    e.target.style.borderColor = '#cbd5e1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedDownloadType !== 'reg_count') {
                    e.target.style.backgroundColor = 'white';
                    e.target.style.borderColor = '#e5e7eb';
                  }
                }}>
                
                <input
                  type="radio"
                  name="downloadType"
                  value="reg_count"
                  checked={selectedDownloadType === 'reg_count'}
                  onChange={(e) => setSelectedDownloadType(e.target.value)}
                  style={{
                    marginRight: '12px',
                    transform: 'scale(1.2)',
                    accentColor: '#0284c7'
                  }} />
                
                <div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '18px' }}>✅</span>
                    Download for Registered Count
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#6b7280'
                  }}>
                    Export data including user registration statistics ({registeredCount} records)
                  </div>
                </div>
              </label>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px',
                  borderRadius: '12px',
                  border: selectedDownloadType === 'log_count' ? '2px solid #0284c7' : '2px solid #e5e7eb',
                  backgroundColor: selectedDownloadType === 'log_count' ? '#f0f9ff' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (selectedDownloadType !== 'log_count') {
                    e.target.style.backgroundColor = '#f8fafc';
                    e.target.style.borderColor = '#cbd5e1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedDownloadType !== 'log_count') {
                    e.target.style.backgroundColor = 'white';
                    e.target.style.borderColor = '#e5e7eb';
                  }
                }}>
                
                <input
                  type="radio"
                  name="downloadType"
                  value="log_count"
                  checked={selectedDownloadType === 'log_count'}
                  onChange={(e) => setSelectedDownloadType(e.target.value)}
                  style={{
                    marginRight: '12px',
                    transform: 'scale(1.2)',
                    accentColor: '#0284c7'
                  }} />
                
                <div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '18px' }}>🆕</span>
                    Download for Login Count
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#6b7280'
                  }}>
                    Export data including login statistics ({firstTimeLoginCount} records)
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: '16px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <Button
              onClick={handleExportModalClose}
              style={{
                backgroundColor: '#f3f4f6',
                borderColor: '#e5e7eb',
                color: '#374151',
                fontWeight: '600',
                padding: '12px 28px',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                border: '1px solid #e5e7eb'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#e5e7eb';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
                e.target.style.transform = 'translateY(0)';
              }}>
              
              ✕ Cancel
            </Button>
            
            <Button
              onClick={handleConfirmExport}
              disabled={exporting}
              style={{
                background: exporting ? '#e5e7eb' : '#1395f2',
                borderColor: 'transparent',
                color: 'white',
                fontWeight: '700',
                padding: '12px 32px',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                boxShadow: exporting ? 'none' : '0 4px 15px rgba(19, 149, 242, 0.4)',
                cursor: exporting ? 'not-allowed' : 'pointer',
                opacity: exporting ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!exporting) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(19, 149, 242, 0.6)';
                }
              }}
              onMouseLeave={(e) => {
                if (!exporting) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(19, 149, 242, 0.4)';
                }
              }}>
              
              {exporting ?
              <>
                  <span style={{ marginRight: '8px' }}>⏳</span>
                  Exporting...
                </> :

              <>
                  <span style={{ marginRight: '8px' }}>📥</span>
                  Download Export
                </>}
              
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </Fragment>);

};

export default LoginAnalytics;